/**
 * Wave 5 Screen 6 — Taška★ bag list: open → offer detail/shop handoff + trash remove
 * (FR-10 / AC-10). Trash distinct from SP ★ and bag-in.
 */

import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '../../shared/ui/Button.js';
import { Card } from '../../shared/ui/Card.js';
import { DirectoryResultsSkeleton } from '../../shared/ui/DirectoryResults.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { useCustomerFormatters } from '../../shared/hooks/useCustomerFormatters.js';
import { isGloballySignedInChrome } from '../hub/customerHubPaths.js';
import { removeOfferFavorite } from '../account/offerFavoritesApi.js';
import {
  removePendingOfferFavorite,
  readPendingOfferFavorites,
} from '../account/pendingOfferFavorites.js';
import { useOfferFavoritesByToken } from '../account/useOfferFavoritesByToken.js';
import type { CustomerOfferFavoriteListItemDTO } from '../account/offerFavoritesTypes.js';

export type BrowseBagResultsProps = {
  /** Bag row open → parent offer detail / shop handoff (FR-10 / AC-10). */
  readonly onOpenItem?: (item: CustomerOfferFavoriteListItemDTO) => void;
};

function BagRow(props: {
  readonly item: CustomerOfferFavoriteListItemDTO;
  readonly formatPrice: (amount: number) => string;
  readonly onOpen: () => void;
  readonly onRemove: () => void;
  readonly removing: boolean;
}): JSX.Element {
  const { t } = useTranslation('customer');
  const { item, formatPrice, onOpen, onRemove, removing } = props;
  const priceLabel =
    item.price !== null ? formatPrice(item.price) : t('browse.discovery.priceUnavailable');
  const thumb = item.thumbnailUrl ?? item.imageUrl;

  return (
    <li
      className="flex min-h-11 items-center gap-3 border-b border-[var(--color-border)] py-3 last:border-b-0"
      data-testid={`customer-browse-bag-row-${item.id}`}
    >
      <button
        type="button"
        className={[
          'flex min-w-0 flex-1 items-center gap-3 rounded-md text-start',
          'hover:bg-[var(--color-surface-hover)]',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
          'focus-visible:outline-[var(--color-focus-ring)]',
        ].join(' ')}
        data-testid={`customer-browse-bag-open-${item.id}`}
        aria-label={t('browse.discovery.bagOpenAria', {
          defaultValue: 'Otevřít nabídku: {{name}}',
          name: item.title,
        })}
        onClick={onOpen}
      >
        {thumb !== null && thumb.length > 0 ? (
          <img
            src={thumb}
            alt=""
            className="h-14 w-14 shrink-0 rounded-md object-cover"
            data-testid={`customer-browse-bag-thumb-${item.id}`}
          />
        ) : (
          <div
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md bg-[var(--color-surface)] text-xs text-[var(--color-on-surface-muted)]"
            data-testid={`customer-browse-bag-thumb-placeholder-${item.id}`}
          >
            —
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p
            className="truncate text-sm font-medium text-[var(--color-on-surface)]"
            data-testid={`customer-browse-bag-title-${item.id}`}
          >
            {item.title}
          </p>
          <p className="truncate text-xs text-[var(--color-on-surface-muted)]">
            {item.salesPointContext.name}
          </p>
          <p className="text-sm text-[var(--color-on-surface)]">{priceLabel}</p>
        </div>
      </button>
      <Button
        type="button"
        intent="ghost"
        size="md"
        disabled={removing}
        aria-label={t('browse.discovery.bagRemoveAria', {
          defaultValue: 'Odebrat z tašky',
          name: item.title,
        })}
        data-testid={`customer-browse-bag-trash-${item.id}`}
        onClick={(event) => {
          event.stopPropagation();
          onRemove();
        }}
      >
        {t('browse.discovery.bagRemove', { defaultValue: 'Smazat' })}
      </Button>
    </li>
  );
}

export function BrowseBagResults(props: BrowseBagResultsProps = {}): JSX.Element {
  const { onOpenItem } = props;
  const { t } = useTranslation('customer');
  const { formatPrice } = useCustomerFormatters();
  const { apiBaseUrl } = useRuntimeConfig();
  const { session } = useCustomerSession();
  const signedIn = isGloballySignedInChrome({ session });
  const queryClient = useQueryClient();
  const bagQuery = useOfferFavoritesByToken(signedIn);

  const guestItems: CustomerOfferFavoriteListItemDTO[] = signedIn
    ? []
    : readPendingOfferFavorites()
        .flatMap((item, index) => {
          if (typeof item.salesPointId !== 'number' || item.salesPointId <= 0) {
            return [];
          }
          const salesPointId = item.salesPointId;
          return [
            {
              id: -(index + 1),
              title: item.title,
              name: item.name,
              imageUrl: item.imageUrl,
              thumbnailUrl: item.thumbnailUrl,
              price: item.price,
              listable: true,
              salesPointContext: {
                salesPointId,
                code: item.salesPointCode,
                name: item.salesPointName,
              },
              productContext: {
                productId: item.productId,
                variantId: item.variantId ?? null,
              },
              tenantContext: {
                tenantId: item.tenantId,
                tenantCode: item.tenantCode,
                tenantName: item.tenantName,
              },
              createdAt: item.seenAt,
            } satisfies CustomerOfferFavoriteListItemDTO,
          ];
        });

  const serverItems = (bagQuery.data?.items ?? []).filter((item) => item.listable);
  const items = signedIn ? serverItems : guestItems;
  const loading = signedIn && bagQuery.isPending;

  const removeMutation = useMutation({
    mutationFn: async (item: CustomerOfferFavoriteListItemDTO) => {
      if (!signedIn) {
        removePendingOfferFavorite({
          salesPointId: item.salesPointContext.salesPointId,
          productId: item.productContext.productId,
          variantId: item.productContext.variantId,
        });
        return;
      }
      await removeOfferFavorite({
        apiBaseUrl,
        body: {
          salesPointId: item.salesPointContext.salesPointId,
          productId: item.productContext.productId,
          variantId: item.productContext.variantId,
        },
      });
    },
    onSuccess: async () => {
      if (signedIn) {
        await queryClient.invalidateQueries({
          queryKey: customerQueryKeys.offerFavorites(
            session?.customerId ?? 'session',
          ),
        });
      }
    },
  });

  const handleRemove = useCallback(
    (item: CustomerOfferFavoriteListItemDTO) => {
      removeMutation.mutate(item);
    },
    [removeMutation],
  );

  const handleOpen = useCallback(
    (item: CustomerOfferFavoriteListItemDTO) => {
      onOpenItem?.(item);
    },
    [onOpenItem],
  );

  if (loading) {
    return (
      <DirectoryResultsSkeleton
        density="list"
        count={3}
        size="lg"
        rowDensity="comfortable"
        listLayout="split2"
        testId="customer-browse-bag-skeleton"
      />
    );
  }

  if (items.length === 0) {
    return (
      <Card padded data-testid="customer-browse-bag-empty">
        <p className="text-sm text-[var(--color-on-surface-muted)]">
          {t('browse.discovery.bagEmptyBody', {
            defaultValue: 'V tašce zatím nic nemáte.',
          })}
        </p>
      </Card>
    );
  }

  return (
    <ul
      className="divide-y divide-[var(--color-border)] rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-3"
      data-testid="customer-browse-bag-list"
      aria-live="polite"
    >
      {items.map((item) => (
        <BagRow
          key={`${item.salesPointContext.salesPointId}-${item.productContext.productId}-${item.productContext.variantId ?? 'base'}`}
          item={item}
          formatPrice={formatPrice}
          removing={removeMutation.isPending}
          onOpen={() => {
            handleOpen(item);
          }}
          onRemove={() => {
            handleRemove(item);
          }}
        />
      ))}
    </ul>
  );
}
